"use client";

import { ArrowRight, CreditCard, MapPin, ShoppingCart, Trash2 } from "lucide-react";
import { Alert, Dialog, EmptyState } from "@nitro/ui";
import { money, typeLabel } from "../lib/shop";
import { useShopCart } from "./ShopCartProvider";

export function ShopCartDrawer() {
  const {
    cart,
    closeCart,
    countryIso2,
    isDrawerOpen,
    quote,
    removeLine,
    state,
    updateQuantity,
  } = useShopCart();

  return (
    <Dialog
      bodyClassName="shop-drawer-body"
      className="shop-drawer"
      closeLabel="Cerrar carrito"
      description={cart.length ? `${cart.length} producto(s)` : "Sin productos"}
      dismissOnBackdrop
      onClose={closeCart}
      open={isDrawerOpen}
      size="sm"
      title={<><ShoppingCart size={18} /> Carrito</>}
    >
      <div className="shop-drawer-content">
        <div className="shop-drawer-lines">
          <CartLines cart={cart} removeLine={removeLine} updateQuantity={updateQuantity} />
        </div>

        <footer className="shop-drawer-foot">
          {state.notice ? <Alert tone="success">{state.notice}</Alert> : null}
          {state.error ? <Alert tone="danger">{state.error}</Alert> : null}
          {quote?.errors?.length ? (
            <Alert tone="danger">
              {quote.errors.map((error) => <span key={error}>{error}</span>)}
            </Alert>
          ) : null}
          {quote?.totals ? (
            <dl className="shop-totals shop-drawer-totals">
              <div><dt>Subtotal</dt><dd>{money(quote.totals.subtotalAmount, countryIso2, quote.totals.currencyCode)}</dd></div>
              <div><dt>Envio</dt><dd>{money(quote.totals.shippingAmount, countryIso2, quote.totals.currencyCode)}</dd></div>
              <div><dt>Total</dt><dd>{money(quote.totals.totalAmount, countryIso2, quote.totals.currencyCode)}</dd></div>
            </dl>
          ) : null}

          <a
            className={`nitro-primary-button shop-checkout-button ${!cart.length ? "is-disabled" : ""}`}
            href={cart.length ? "/tienda/carrito" : "#"}
            onClick={(event) => {
              if (!cart.length) {
                event.preventDefault();
                return;
              }
              closeCart();
            }}
          >
            Continuar al carrito <ArrowRight size={16} />
          </a>
          <button className="shop-drawer-secondary-button" type="button" onClick={closeCart}><ArrowRight size={16} /> Seguir comprando</button>
        </footer>
      </div>
    </Dialog>
  );
}

export function CartLines({ cart, removeLine, updateQuantity }) {
  if (!cart.length) {
    return (
      <EmptyState
        description="Abre un producto y agrega una seleccion para iniciar la compra."
        size="compact"
        title="Tu carrito esta vacio."
      />
    );
  }

  return cart.map((item) => (
    <article className="shop-drawer-line" key={item.key}>
      <div className="shop-drawer-line-image">
        {item.imageUrl ? <img alt="" src={item.imageUrl} /> : <ShoppingCart size={22} />}
      </div>
      <div className="shop-drawer-line-copy">
        <strong>{item.name}</strong>
        <span>{[item.variantName, typeLabel(item.type)].filter(Boolean).join(" / ")}</span>
        <div className="shop-drawer-line-actions">
          <input aria-label={`Cantidad de ${cartItemAccessibleName(item)}`} min="0" max={item.maxQuantity || 9} type="number" value={item.quantity} onChange={(event) => updateQuantity(item.key, event.target.value)} />
          <button aria-label={`Quitar ${cartItemAccessibleName(item)} del carrito`} type="button" onClick={() => removeLine(item.key)}><Trash2 size={14} /> Quitar</button>
        </div>
      </div>
    </article>
  ));
}

function cartItemAccessibleName(item) {
  return [item?.name, item?.variantName].filter(Boolean).join(", ") || "producto";
}

export function CartMiniSummary({ cart }) {
  return (
    <div className="shop-mini-summary">
      {cart.map((item) => (
        <div key={item.key}>
          <span>{item.name}</span>
          <strong>x{item.quantity}</strong>
        </div>
      ))}
    </div>
  );
}

export function ShippingAddressForm({ address, onChange }) {
  function update(field, value) {
    onChange((current) => ({ ...current, [field]: value }));
  }

  return (
    <div className="shop-address-form">
      <h4><MapPin size={16} /> Direccion de envio</h4>
      <input placeholder="Nombre de quien recibe" value={address.recipientName} onChange={(event) => update("recipientName", event.target.value)} />
      <input placeholder="Telefono" value={address.phone} onChange={(event) => update("phone", event.target.value)} />
      <input placeholder="Ciudad" value={address.city} onChange={(event) => update("city", event.target.value)} />
      <input placeholder="Direccion" value={address.line1} onChange={(event) => update("line1", event.target.value)} />
    </div>
  );
}

export function ShippingMethodSelector({ countryIso2, methods, selectedCode, onChange }) {
  if (!methods.length) {
    return <EmptyState size="compact" title="No hay metodo de envio disponible para esta compra." />;
  }

  return (
    <div className="shop-address-form">
      <h4><MapPin size={16} /> Metodo de envio</h4>
      <div className="shop-drawer-payment-list">
        {methods.map((method) => (
          <label className="shop-drawer-payment is-selectable" key={method.code}>
            <input
              checked={selectedCode === method.code}
              name="shop-shipping-method"
              onChange={() => onChange(method.code)}
              type="radio"
            />
            <span>
              <strong>{method.displayName}</strong>
              <small>{shippingMethodText(method, countryIso2)}</small>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

export function PaymentMethodSelector({ countryIso2, methods, selectedCode, onChange }) {
  if (!methods.length) {
    return (
      <EmptyState icon={<CreditCard size={16} />} size="compact" title="No hay metodo de pago disponible." />
    );
  }

  return (
    <div className="shop-drawer-payment-list">
      {methods.map((method) => (
        <label className="shop-drawer-payment is-selectable" key={method.code}>
          <input
            checked={selectedCode === method.code}
            name="shop-payment-method"
            onChange={() => onChange(method.code)}
            type="radio"
          />
          <CreditCard size={16} />
          <span>
            <strong>{method.displayName}</strong>
            <small>{method.currencyCode}</small>
          </span>
        </label>
      ))}
    </div>
  );
}

function shippingMethodText(method, countryIso2) {
  const cost = money(method.shippingAmount, countryIso2, method.currencyCode);
  const days = method.estimatedDaysMin && method.estimatedDaysMax
    ? `${method.estimatedDaysMin}-${method.estimatedDaysMax} dias`
    : "Tiempo por confirmar";
  return `${cost} / ${days}`;
}
